"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BarChart3,
  Bitcoin,
  Globe,
  Landmark,
  ShieldAlert,
  Star,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import { Sparkline } from "@/components/Sparkline";
import { api, loadTokens, type PiLeaderboardRow } from "@/lib/api";
import { loginUrl } from "@/lib/loginUrl";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";
import {
  formatUsdcCentsForDisplay,
  type MoneyCurrency,
} from "@/lib/format";
import { PI_TIERS, piTierMeta } from "@/lib/piTier";

/**
 * EPIC-005 STORY-509 + 2026-05-02 demo-parity redesign.
 *
 * Phase 1 (this commit): layout matches `granbay-v5_4.html` demo —
 * hero card with PIP description + 4 tier cards + Apply CTA, sort
 * tabs row, card-grid replacing the table. Tier names remap from
 * the API's bronze/silver/gold/diamond to demo's
 * Cadet/Champion/Elite/Elite Pro (same ordering, different copy).
 * Card stats use the fields the leaderboard API exposes today
 * (volume, trades, copiers); demo's AUM / 30-day return / win rate
 * / risk score / sparkline / category tags wait on Phase 2 backend
 * work — see todo PI-PHASE-2.
 *
 * Auth-required for closed beta (matches /pi/[username] privacy
 * posture; truly-public leaderboard ships post-Gate-A with the
 * privacy-and-opt-in story EPIC-021 STORY-381).
 */
export default function PiLeaderboardPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const displayCurrency = useDisplayCurrency();
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  const [authChecked, setAuthChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [rows, setRows] = useState<PiLeaderboardRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<"volume" | "copiers" | "trades">("volume");
  // STORY-503 phase 2 — per-PI active sub lookup. Lets each card flip
  // its primary CTA from "Copy Now" → "View activity" when the user
  // is already copying that PI. Map keyed by pi user_id → sub_id.
  const [activeSubByPi, setActiveSubByPi] = useState<Map<string, string>>(
    new Map(),
  );
  // Founder bug 2026-05-01: hide the "Apply to PIP" CTA when the
  // signed-in user is already in the program (or has a pending app).
  // Mirrors the PiApplicationBanner gate.
  const [hasActivePiApp, setHasActivePiApp] = useState(false);

  useEffect(() => {
    if (!loadTokens()) {
      router.replace(loginUrl());
      return;
    }
    void api
      .me()
      .then(() => {
        setAuthChecked(true);
        setAuthorized(true);
      })
      .catch(() => router.replace(loginUrl()));
  }, [router]);

  useEffect(() => {
    if (!authorized) return;
    let cancelled = false;
    void api
      .getPiLeaderboard()
      .then((res) => {
        if (!cancelled) setRows(res.rows);
      })
      .catch((err) => {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : t("piLeaderboard.loadFailed"),
          );
      });
    // STORY-503 phase 2 — load active subs so each card can swap
    // Copy Now → View activity. Best-effort: failure leaves the map
    // empty (cards default to Copy Now, never a misleading state).
    void api
      .listMyCopySubscriptions(false)
      .then((subs) => {
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const s of subs) {
          if (!s.unsubscribedAt) map.set(s.piUserId, s.id);
        }
        setActiveSubByPi(map);
      })
      .catch(() => {
        /* silent — leaderboard works without this */
      });
    // Suppress the Apply-to-PIP CTA when the user is already an
    // approved PI or has a pending application in flight. Failure of
    // the lookup leaves the CTA visible (safe default — better to
    // show a redundant CTA than hide it from a non-PI).
    void api
      .listMyPiApplications()
      .then((apps) => {
        if (cancelled) return;
        const active = apps.some(
          (a) => a.status === "pending" || a.status === "approved",
        );
        setHasActivePiApp(active);
      })
      .catch(() => {
        // Best-effort lookup; default to "show CTA".
      });
    return () => {
      cancelled = true;
    };
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized]);

  // Frontend-side sort. Returning a fresh array each time so React
  // notices the change; ranking respects the chip choice.
  const sortedRows = useMemo(() => {
    if (!rows) return null;
    const copy = [...rows];
    switch (sort) {
      case "volume":
        copy.sort(
          (a, b) =>
            Number(b.lifetimeVolumeCents) - Number(a.lifetimeVolumeCents),
        );
        break;
      case "copiers":
        copy.sort((a, b) => b.activeSubscriberCount - a.activeSubscriberCount);
        break;
      case "trades":
        copy.sort((a, b) => b.lifetimeTradeCount - a.lifetimeTradeCount);
        break;
    }
    return copy;
  }, [rows, sort]);

  if (!authChecked) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto max-w-[800px] p-8 text-sm text-[var(--color-text3)]">
          {t("piLeaderboard.loading")}
        </main>
      </>
    );
  }
  if (!authorized) return null;

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0">
          {/* Hero card — PIP overview + 4 tier cards + Apply CTA.
              Demo parity (granbay-v5_4.html). The percentages and
              caps are static copy taken from the demo's PI_TIERS
              definition. Founder controls the actual rate config
              via /admin/fees → STORY-902. */}
          <section className="mb-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
            <div className="mb-2 flex items-center gap-2">
              <Star
                size={16}
                strokeWidth={1.5}
                aria-hidden="true"
                style={{ color: "var(--color-brand)" }}
              />
              <h1 className="text-[18px] font-bold tracking-tight text-[var(--color-text)]">
                {t("piLeaderboard.pipTitle")}
              </h1>
            </div>
            <p className="mb-4 max-w-[720px] text-[12px] leading-relaxed text-[var(--color-text2)]">
              {t("piLeaderboard.pipDesc")}
            </p>
            <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {PI_TIERS.map((tier) => (
                <div
                  key={tier.key}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3"
                >
                  <div
                    className="mb-1 text-[12px] font-bold"
                    style={{ color: tier.color }}
                  >
                    {t(tier.labelKey)}
                  </div>
                  <div className="text-[10px] leading-relaxed text-[var(--color-text3)]">
                    {tier.bps / 100}% {t("piLeaderboard.pipPctOfAum")}
                    <br />
                    {t("piLeaderboard.pipCap")}{" "}
                    {tier.capUsd === null
                      ? t("piLeaderboard.pipUnlimited")
                      : `$${(tier.capUsd / 1000).toFixed(0)}k/mo`}
                  </div>
                </div>
              ))}
            </div>
            {hasActivePiApp ? (
              <p className="text-[11px] text-[var(--color-text3)]">
                {t("piLeaderboard.pipAlreadyMember")}
              </p>
            ) : (
              <Link
                href="/pi/apply"
                className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-brand)] px-3.5 py-1.5 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
              >
                {t("piLeaderboard.pipApply")}
                <ArrowRight size={14} strokeWidth={1.75} aria-hidden="true" />
              </Link>
            )}
          </section>

          {/* Sort + filter tabs — sort tabs are wired to the
              frontend-side sort over fields we have today. Category
              filters (Politics / Sports / Crypto) are rendered
              disabled with a "coming soon" tooltip until the
              `pi_categories` field lands in Phase 2. Same approach
              the demo took: chips visible to set expectations. */}
          <div className="mb-4 flex flex-wrap items-center gap-1.5">
            <SortPill
              active={sort === "volume"}
              onClick={() => setSort("volume")}
              icon={
                <BarChart3 size={12} strokeWidth={1.75} aria-hidden="true" />
              }
            >
              {t("piLeaderboard.sortByVolume")}
            </SortPill>
            <SortPill
              active={sort === "copiers"}
              onClick={() => setSort("copiers")}
              icon={<Users size={12} strokeWidth={1.75} aria-hidden="true" />}
            >
              {t("piLeaderboard.sortByCopiers")}
            </SortPill>
            <SortPill
              active={sort === "trades"}
              onClick={() => setSort("trades")}
              icon={<Zap size={12} strokeWidth={1.75} aria-hidden="true" />}
            >
              {t("piLeaderboard.sortByTrades")}
            </SortPill>
            <span
              aria-hidden
              className="mx-1 h-4 w-px bg-[var(--color-border)]"
            />
            <CategoryPlaceholderPill
              icon={<Globe size={12} strokeWidth={1.75} aria-hidden="true" />}
            >
              {t("piLeaderboard.filterAll")}
            </CategoryPlaceholderPill>
            <CategoryPlaceholderPill
              disabled
              icon={
                <Landmark size={12} strokeWidth={1.75} aria-hidden="true" />
              }
            >
              {t("piLeaderboard.filterPolitics")}
            </CategoryPlaceholderPill>
            <CategoryPlaceholderPill
              disabled
              icon={<Trophy size={12} strokeWidth={1.75} aria-hidden="true" />}
            >
              {t("piLeaderboard.filterSports")}
            </CategoryPlaceholderPill>
            <CategoryPlaceholderPill
              disabled
              icon={<Bitcoin size={12} strokeWidth={1.75} aria-hidden="true" />}
            >
              {t("piLeaderboard.filterCrypto")}
            </CategoryPlaceholderPill>
          </div>

          {error && (
            <p
              role="alert"
              className="mb-4 text-[12px]"
              style={{ color: "var(--color-no)" }}
            >
              {error}
            </p>
          )}

          {!sortedRows && !error && (
            <p className="text-[12px] text-[var(--color-text3)]">
              {t("piLeaderboard.loading")}
            </p>
          )}

          {sortedRows && sortedRows.length === 0 && (
            <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-5 text-[13px] text-[var(--color-text2)]">
              {t("piLeaderboard.emptyPrefix")}{" "}
              <Link
                href="/pi/apply"
                className="font-bold text-[var(--color-brand)] hover:underline"
              >
                {t("piLeaderboard.emptyLink")}
              </Link>
              {t("piLeaderboard.emptySuffix")}
            </p>
          )}

          {sortedRows && sortedRows.length > 0 && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {sortedRows.map((row) => (
                <PiCard
                  key={row.userId}
                  row={row}
                  displayCurrency={displayCurrency}
                  usdcPerBrl={usdcPerBrl}
                  activeSubId={activeSubByPi.get(row.userId) ?? null}
                  t={t}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
}

// ─── Sort + filter pill components ─────────────────────────────

function SortPill({
  active,
  onClick,
  children,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
        active
          ? "border-[var(--color-text2)] bg-[var(--color-bg)] text-[var(--color-text)]"
          : "border-[var(--color-border)] text-[var(--color-text2)] hover:border-[var(--color-text3)]"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function CategoryPlaceholderPill({
  children,
  disabled,
  icon,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
}) {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <span
        title={t("piLeaderboard.filterComingSoon")}
        aria-disabled="true"
        className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-[11px] font-semibold text-[var(--color-text3)] opacity-50"
      >
        {icon}
        {children}
      </span>
    );
  }
  return (
    <span
      aria-pressed="true"
      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-text2)] bg-[var(--color-bg)] px-3 py-1.5 text-[11px] font-semibold text-[var(--color-text)]"
    >
      {icon}
      {children}
    </span>
  );
}

// ─── PI card ───────────────────────────────────────────────────

function PiCard({
  row,
  displayCurrency,
  usdcPerBrl,
  activeSubId,
  t,
}: {
  row: PiLeaderboardRow;
  displayCurrency: MoneyCurrency;
  usdcPerBrl: number | null;
  activeSubId: string | null;
  t: (key: string) => string;
}) {
  const tier = piTierMeta(row.piTier);
  const initial = row.username[0]?.toUpperCase() ?? "?";
  // PI-PHASE-2 — avatar gradient prefers the deterministic
  // sha256(username) hue (so each PI has a unique consistent color
  // tone) but falls back to the tier color until the cron has
  // populated avatar_hue.
  const avatarStart =
    row.avatarHue !== null ? `hsl(${row.avatarHue}, 70%, 50%)` : tier.color;
  const avatarEnd =
    row.avatarHue !== null
      ? `hsl(${row.avatarHue}, 70%, 30%)`
      : `${tier.color}88`;
  // Sparkline cents come back as string[] (BIGINT). Convert + show
  // only if we have ≥2 points (Sparkline component returns null
  // otherwise so it's safe but the wrapper saves an unused render).
  const sparklinePoints =
    row.sparkline30dCents.length >= 2
      ? row.sparkline30dCents.map((s) => Number(s))
      : null;
  // Founder hard rule: risk_score ≥ 8 disables the Copy button.
  const uncopyableRisk = row.riskScore !== null && row.riskScore >= 8;

  // Categories — render up to 2 chips when populated. Manual override
  // already merged into primary/secondary by the backend.
  const categories: string[] = [];
  if (row.primaryCategory) categories.push(row.primaryCategory);
  if (row.secondaryCategory) categories.push(row.secondaryCategory);

  // 30-day return: backend emits as string ("12.40"); coerce + format.
  const return30d =
    row.return30dPct !== null && row.return30dPct !== undefined
      ? Number(row.return30dPct)
      : null;
  const winRate30d =
    row.winRate30dPct !== null && row.winRate30dPct !== undefined
      ? Number(row.winRate30dPct)
      : null;

  return (
    <div className="flex flex-col rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3.5 transition-colors hover:border-[var(--color-border2)]">
      <div className="mb-3 flex items-start gap-3">
        <div
          aria-hidden
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-[14px] font-bold text-white"
          style={{
            background: `linear-gradient(135deg, ${avatarStart}, ${avatarEnd})`,
          }}
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-[14px] font-bold text-[var(--color-text)]">
              {row.username}
            </span>
            <span
              className="rounded-[4px] px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.4px]"
              style={{
                background: `color-mix(in srgb, ${tier.color} 14%, transparent)`,
                color: tier.color,
              }}
            >
              {t(tier.labelKey)}
            </span>
            {/* Risk badge — only when populated. Yellow (caution)
                at 6-7, red (alert) at ≥8 (the uncopyable threshold). */}
            {row.riskScore !== null && (
              <span
                className="inline-flex items-center gap-0.5 rounded-[4px] px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.4px]"
                style={{
                  background:
                    row.riskScore >= 8
                      ? "color-mix(in srgb, var(--color-status-negative) 14%, transparent)"
                      : row.riskScore >= 6
                        ? "color-mix(in srgb, var(--color-status-caution) 14%, transparent)"
                        : "color-mix(in srgb, var(--color-text3) 14%, transparent)",
                  color:
                    row.riskScore >= 8
                      ? "var(--color-status-negative)"
                      : row.riskScore >= 6
                        ? "var(--color-status-caution)"
                        : "var(--color-text3)",
                }}
                title={
                  row.riskScoreLimitedHistory
                    ? `Risk ${row.riskScore}/10 — limited history (<90d)`
                    : `Risk ${row.riskScore}/10 (90d drawdown)`
                }
              >
                <ShieldAlert size={9} strokeWidth={2} aria-hidden="true" />
                {row.riskScore}/10
              </span>
            )}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-[var(--color-text3)]">
            @{row.username}
            {row.country ? ` · ${row.country}` : ""}
          </div>
          {categories.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {categories.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-[0.4px] text-[var(--color-text3)]"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sparkline (30d NAV cents) — renders only when the cron has
          populated ≥2 points; otherwise the row collapses cleanly. */}
      {sparklinePoints && (
        <div className="mb-2">
          <Sparkline
            points={sparklinePoints}
            width={260}
            height={32}
            ariaLabel={`${row.username} 30-day NAV trend`}
          />
        </div>
      )}

      {/* Stat tiles — switches between Phase-1 fallback set
          (Volume / Copiers / Trades) and Phase-2 set (30d Return /
          Win Rate / Copiers) per which fields the cron has
          populated. AUM gets surfaced when available, replacing
          Volume as the headline aggregate. */}
      <div className="mb-3 grid grid-cols-3 gap-1.5">
        <Stat
          label={row.aumCents !== null ? "AUM" : t("piLeaderboard.cardVolume")}
          value={formatVolumeForDisplay(
            row.aumCents ?? row.lifetimeVolumeCents,
            displayCurrency,
            usdcPerBrl,
          )}
        />
        {return30d !== null ? (
          <Stat
            label={t("piLeaderboard.cardReturn30d")}
            value={`${return30d >= 0 ? "+" : ""}${return30d.toFixed(1)}%`}
            tone={return30d >= 0 ? "positive" : "negative"}
          />
        ) : (
          <Stat
            label={t("piLeaderboard.cardCopiers")}
            value={row.activeSubscriberCount.toLocaleString()}
          />
        )}
        {winRate30d !== null ? (
          <Stat
            label={t("piLeaderboard.cardWinRate")}
            value={`${winRate30d.toFixed(0)}%`}
          />
        ) : (
          <Stat
            label={t("piLeaderboard.cardTrades")}
            value={row.lifetimeTradeCount.toLocaleString()}
          />
        )}
      </div>

      <div className="mt-auto flex gap-1.5">
        {/* STORY-503 phase 2 — when the user already has an active sub
            to this PI, the Copy Now CTA flips to View activity and
            links to /portfolio/copy/{subId}/activity. Prevents the
            confusing "Copy" appearing on a PI you're already copying. */}
        {activeSubId ? (
          <Link
            href={`/portfolio/copy/${activeSubId}/activity`}
            className="flex min-h-[40px] flex-1 items-center justify-center rounded-md bg-[#2d8a5d] py-1.5 text-center text-[11px] font-bold text-white transition-opacity hover:opacity-90 md:min-h-0"
          >
            {t("piLeaderboard.cardViewActivity")}
          </Link>
        ) : uncopyableRisk ? (
          <span
            title={t("piLeaderboard.uncopyableTooltip")}
            className="flex min-h-[40px] flex-1 cursor-not-allowed items-center justify-center rounded-md border border-[var(--color-border)] py-1.5 text-center text-[11px] font-bold text-[var(--color-text3)] opacity-60 md:min-h-0"
          >
            {t("piLeaderboard.cardCopyNow")}
          </span>
        ) : (
          <Link
            href={`/pi/${row.username}`}
            className="flex min-h-[40px] flex-1 items-center justify-center rounded-md bg-[var(--color-brand)] py-1.5 text-center text-[11px] font-bold text-white transition-opacity hover:opacity-90 md:min-h-0"
          >
            {t("piLeaderboard.cardCopyNow")}
          </Link>
        )}
        <Link
          href={`/pi/${row.username}`}
          className="flex min-h-[40px] flex-1 items-center justify-center rounded-md border border-[var(--color-border)] py-1.5 text-center text-[11px] font-semibold text-[var(--color-text2)] transition-colors hover:border-[var(--color-text2)] hover:text-[var(--color-text)] md:min-h-0"
        >
          {t("piLeaderboard.cardViewProfile")}
        </Link>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative";
}) {
  const valueColor =
    tone === "positive"
      ? "var(--color-status-positive, #15803d)"
      : tone === "negative"
        ? "var(--color-status-negative, #dc2626)"
        : "var(--color-text)";
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-center">
      <div className="text-[9px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {label}
      </div>
      <div
        className="mt-0.5 font-mono text-[12px] font-bold tabular-nums"
        style={{ color: valueColor }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * EPIC-029 STORY-1805 follow-up — fx-resolve volume into the user's
 * display_currency. Source is USDC (the matcher unit post STORY-
 * 1700-part-3); BRL display multiplies by the rate. While the rate
 * loads, fall back to USDC formatting so the row doesn't flash "—".
 *
 * CAVEAT: PI volume aggregations SUM over historical trades; legacy
 * pre-Part-3 BRL rows in the SUM render 5× understated until the
 * data-layer backfill ships.
 */
function formatVolumeForDisplay(
  cents: string,
  displayCurrency: MoneyCurrency,
  usdcPerBrl: number | null,
): string {
  return formatUsdcCentsForDisplay(Number(cents), displayCurrency, usdcPerBrl, {
    compact: true,
  });
}
